/**
 * T-165: App as the handoff SOURCE (docs/auth/handoff-contract.md §10,
 * "App web → Sales"). A Sales search result opens Sales already signed in:
 *
 *   click → blank tab (V11: synchronously, or Safari drops the user activation
 *   across the awaited POST) → POST Sales' issue endpoint with App's bearer →
 *   tab → https://sales.hollywoodtrucks.com/auth/handoff#h=<code>
 *
 * Anything but a clean 201 sends the tab to the plain URL, so a handoff can
 * never strand a blank tab or open something the user didn't click.
 *
 * Sales' CORS trusts exactly https://app.hollywoodtrucks.com and preflights
 * only `authorization, content-type`, so the request carries nothing else and
 * no cookies. On any other App host (DEMO, previews, localhost) the bearer
 * belongs to a different project or origin, so those just open the plain URL.
 */

export const SALES_ORIGIN = 'https://sales.hollywoodtrucks.com';
export const APP_PROD_ORIGIN = 'https://app.hollywoodtrucks.com';
/** Sales refuses a token issued more than 300 s ago; refresh a little before that. */
const MAX_TOKEN_AGE_S = 240;
/** Sales' codes are opaque (base64url today); only URL-safe characters go in the fragment. */
const CODE_RE = /^[A-Za-z0-9_-]{16,256}$/;

type HandoffTab = { opener: unknown; closed?: boolean; location: { replace(url: string): void } };

export interface OpenSalesDeps {
  appOrigin: string;
  now: () => number;
  openWindow: (url: string, target: string, features?: string) => HandoffTab | null;
  getAccessToken: () => Promise<string | null>;
  refreshAccessToken: () => Promise<string | null>;
  fetch: (url: string, init: RequestInit) => Promise<Response>;
  timeoutMs: number;
}

/** The token's `iat` in seconds, or null when it can't be read. */
function issuedAt(token: string): number | null {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '='));
    const iat = JSON.parse(json).iat;
    return Number.isFinite(iat) ? iat : null;
  } catch {
    return null;
  }
}

function isSalesUrl(href: string): boolean {
  try {
    return new URL(href).origin === SALES_ORIGIN;
  } catch {
    return false;
  }
}

async function freshToken(deps: OpenSalesDeps): Promise<string | null> {
  const token = await deps.getAccessToken();
  const iat = token ? issuedAt(token) : null;
  if (token && iat !== null && deps.now() / 1000 - iat <= MAX_TOKEN_AGE_S) return token;
  return deps.refreshAccessToken();
}

/**
 * One issue call: its status and, on a clean 201, the code. The timeout stays
 * armed until the BODY is read too: fetch resolves on the headers, so a body
 * that stalls after them would otherwise hang the blank tab.
 */
async function requestCode(href: string, token: string, deps: OpenSalesDeps): Promise<{ status: number; code: string | null }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), deps.timeoutMs);
  try {
    const res = await deps.fetch(`${SALES_ORIGIN}/api/v1/auth/handoff`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ next: href, client: 'app' }),
      credentials: 'omit',
      signal: controller.signal,
    });
    return { status: res.status, code: await codeFrom(res) };
  } finally {
    clearTimeout(timer);
  }
}

/** The one-time code from a 201, or null for anything else. */
async function codeFrom(res: Response): Promise<string | null> {
  if (res.status !== 201) return null;
  try {
    const body = await res.json();
    return typeof body?.code === 'string' && CODE_RE.test(body.code) ? body.code : null;
  } catch {
    return null;
  }
}

async function handOff(tab: HandoffTab, href: string, deps: OpenSalesDeps): Promise<void> {
  let target = href;
  try {
    let token = await freshToken(deps);
    if (token) {
      let answer = await requestCode(href, token, deps);
      if (answer.status === 401) {
        // unauthorized / stale_token: refresh once and retry (contract §9).
        token = await deps.refreshAccessToken();
        answer = token ? await requestCode(href, token, deps) : answer;
      }
      if (answer.code) target = `${SALES_ORIGIN}/auth/handoff#h=${answer.code}`;
    }
  } catch {
    // Network error, timeout or a bad body: the plain URL below.
  }
  // The user may have closed the tab while the code was on its way; then there
  // is nothing to steer, and a throw here would be an unhandled rejection.
  if (tab.closed) return;
  try {
    tab.location.replace(target);
  } catch {
    // The tab went away between the check and the redirect.
  }
}

const defaultDeps = (): OpenSalesDeps => ({
  appOrigin: window.location.origin,
  now: () => Date.now(),
  openWindow: (url, target, features) => window.open(url, target, features) as HandoffTab | null,
  getAccessToken: async () => {
    const { supabase } = await import('@/lib/supabase');
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  },
  refreshAccessToken: async () => {
    const { supabase } = await import('@/lib/supabase');
    const { data } = await supabase.auth.refreshSession();
    return data.session?.access_token ?? null;
  },
  fetch: (url, init) => window.fetch(url, init),
  timeoutMs: 8000,
});

/**
 * Open a Sales-owned result. Call it directly from the click handler: the tab
 * must open before anything is awaited.
 */
export function openSalesResult(href: string, deps: OpenSalesDeps = defaultDeps()): void {
  if (deps.appOrigin !== APP_PROD_ORIGIN || !isSalesUrl(href)) {
    deps.openWindow(href, '_blank', 'noopener');
    return;
  }
  // V11: a blank tab now, while the click's user activation is live. Without
  // 'noopener' (which would return null) the opener is cut by hand instead.
  const tab = deps.openWindow('about:blank', '_blank');
  if (!tab) return; // blocked: nothing to steer
  try { tab.opener = null; } catch { /* cross-origin already */ }
  void handOff(tab, href, deps);
}
